import { Link, NavLink, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { useAuth } from '../hooks/useAuth.jsx';
import { LEGAL } from '@affily/shared/constants';
import { Button } from './ui.jsx';
import { BrandSwitcher } from './BrandSwitcher.jsx';

function Wordmark({ className = '' }) {
  return (
    <Link to="/" className={`flex items-center gap-2 ${className}`}>
      <svg viewBox="0 0 32 32" className="size-7 rounded-md" aria-hidden="true">
        <rect width="32" height="32" rx="7" className="fill-accent" />
        <path d="M9 23V9.6c0-.33.27-.6.6-.6H23v3.2h-10.2v3.6H21v3.2h-8.2V23z" fill="#fff" />
      </svg>
      <span className="text-lg font-semibold tracking-tight">Affily</span>
    </Link>
  );
}

const publicNav = [
  { to: '/programs', label: 'Programs' },
  { to: '/creators', label: 'Creators' },
  { to: '/for-creators', label: 'For creators' },
  { to: '/for-brands', label: 'For brands' },
  { to: '/blog', label: 'Blog' },
];

export function Header() {
  const { user, logout, loading } = useAuth();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  return (
    <header className="sticky top-0 z-40 border-b border-rule bg-paper/85 backdrop-blur">
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-6 px-4 sm:px-6">
        <Wordmark />

        <nav className="hidden items-center gap-1 md:flex">
          {publicNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `rounded-md px-2.5 py-1.5 text-sm transition-colors ${
                  isActive ? 'text-ink font-medium' : 'text-ink-muted hover:text-ink'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {loading ? null : user ? (
            <>
              {/* On a phone it lives in the menu instead; the bar has no room. */}
              <BrandSwitcher className="hidden sm:flex" />
              <Button to="/dashboard" variant="ghost" size="sm">
                Dashboard
              </Button>
              <Button onClick={logout} variant="outline" size="sm">
                Sign out
              </Button>
            </>
          ) : (
            <>
              <Button to="/login" variant="ghost" size="sm" className="hidden sm:inline-flex">
                Sign in
              </Button>
              <Button to="/signup" size="sm">
                Join free
              </Button>
            </>
          )}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            aria-expanded={open}
            className="rounded-md p-2 text-ink-muted hover:bg-paper-sunken md:hidden"
          >
            <svg viewBox="0 0 24 24" className="size-5" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-rule bg-paper-raised px-4 py-2 md:hidden">
          {user && <BrandSwitcher className="py-2 sm:hidden" />}
          {publicNav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className={`block rounded-md px-2 py-2 text-sm ${
                pathname === item.to ? 'font-medium text-ink' : 'text-ink-muted'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
}

const footerCols = [
  {
    title: 'Marketplace',
    links: [
      { to: '/programs', label: 'Browse programs' },
      { to: '/creators', label: 'Browse creators' },
      // Affily Boost (/boost) stays routed and prerendered but is not linked
      // until there is something to buy.
    ],
  },
  {
    title: 'Free tools',
    links: [
      { to: '/tools/engagement-rate-calculator', label: 'Engagement rate calculator' },
      { to: '/tools/media-kit-generator', label: 'Media kit generator' },
      { to: '/tools/affiliate-earnings-calculator', label: 'Earnings calculator' },
      { to: '/tools/affiliate-program-calculator', label: 'Program cost calculator' },
    ],
  },
  {
    title: 'About',
    links: [
      { to: '/for-creators', label: 'For creators' },
      { to: '/for-brands', label: 'For brands' },
      { to: '/for-agents', label: 'Use with your agent' },
      { to: '/transparency', label: 'How transparency works' },
      { to: '/help', label: 'Help' },
      { to: '/blog', label: 'Blog' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { to: '/terms', label: 'Terms of Service' },
      { to: '/privacy', label: 'Privacy Policy' },
      { href: `mailto:${LEGAL.contactEmail}`, label: 'Contact us' },
    ],
  },
];

export function Footer() {
  return (
    <footer className="mt-20 border-t border-rule bg-paper-sunken">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
        <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-5">
          <div>
            <Wordmark />
            <p className="mt-3 max-w-xs text-sm text-ink-muted">
              Creators and brands. Clear terms, no pay-to-play rankings, no vanity metrics.
            </p>
          </div>
          {footerCols.map((col) => (
            <div key={col.title}>
              <h3 className="text-2xs font-semibold uppercase tracking-wider text-ink-faint">
                {col.title}
              </h3>
              <ul className="mt-3 space-y-2">
                {col.links.map((l) => (
                  <li key={l.to ?? l.href}>
                    {l.href ? (
                      <a href={l.href} className="text-sm text-ink-muted hover:text-ink">
                        {l.label}
                      </a>
                    ) : (
                      <Link to={l.to} className="text-sm text-ink-muted hover:text-ink">
                        {l.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-10 flex flex-col gap-2 border-t border-rule pt-6 text-xs text-ink-faint sm:flex-row sm:items-center sm:justify-between">
          <p>© {new Date().getFullYear()} Affily, operated by {LEGAL.entityName}. Free to join, both sides.</p>
          <p>Fees are shown in your account, before anything is paid.</p>
        </div>
      </div>
    </footer>
  );
}

/** Public shell: header, content, footer. */
export function PublicLayout({ children }) {
  return (
    <div className="flex min-h-dvh flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}
